import { useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { FiSearch, FiMapPin, FiCheckCircle } from "react-icons/fi";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import Card from "@/components/shared/Card";
import SearchComboBox, {
  ComboBoxOption,
} from "@/components/shared/forms/SearchComboBox";
import { ReviewSection, ReviewField } from "@/components/shared/ReviewSection";
import { formatDateOnly } from "@/utils/dates";
import { GlobalStateModel } from "@/models";
import { useChildEntities } from "@/queries";
import {
  VIEW_TYPE_OPTIONS,
  fetchAgencyData,
  AgencyDataResult,
} from "@/queries/agencyDataApi";
import { STATUS_COLORS } from "@/components/state-level-report/report-2-1/Reports2_1";

const thStyle: React.CSSProperties = {
  color: "#334155",
  fontWeight: 600,
  padding: "10px 12px",
  borderBottom: "1px solid #dee2e6",
  backgroundColor: "#eef2f6",
  fontSize: "0.8125rem",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderBottom: "1px solid #dee2e6",
  fontSize: "0.8125rem",
  color: "#0f1923",
  verticalAlign: "middle",
};

const badgeStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "3px 9px",
  borderRadius: "999px",
  fontSize: "0.72rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

function DataTable({
  columns,
  rows,
  emptyMessage,
}: {
  columns: string[];
  rows: React.ReactNode[][];
  emptyMessage: string;
}) {
  return (
    <div
      className="table-responsive"
      style={{
        border: "1px solid #dee2e6",
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col} style={thStyle}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{
                  ...tdStyle,
                  textAlign: "center",
                  color: "#6c757d",
                  padding: "2rem 1rem",
                }}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={{
                  backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f6f8fa",
                }}
              >
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} style={tdStyle}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export function AgencyDataSearchView() {
  const leaContext = useRouteContext({ from: "/$leaid" }) as GlobalStateModel;
  const leaList = useChildEntities("0-").data;

  const leaComboOptions: ComboBoxOption[] = leaList.map((lea) => ({
    value: lea.LEAID.toString(),
    label: lea.LEAName,
    sublabel: `LEA ID: ${lea.CDSCode}`,
    displayId: lea.CDSCode,
  }));

  const [selectedLeaId, setSelectedLeaId] = useState("");
  const [viewType, setViewType] = useState(
    VIEW_TYPE_OPTIONS[0]?.Value ?? "Agency"
  );
  const [validationError, setValidationError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [result, setResult] = useState<AgencyDataResult | null>(null);

  const handleLeaChange = (value: string) => {
    setSelectedLeaId(value);
    if (validationError) setValidationError("");
    if (searchError) setSearchError("");
    if (hasSearched) setHasSearched(false);
    if (result) setResult(null);
  };

  const handleSearch = async () => {
    if (!selectedLeaId) {
      setValidationError("Please select an LEA.");
      return;
    }

    const lea = leaList.find((x) => x.LEAID.toString() === selectedLeaId);

    if (!lea) {
      setValidationError("Please select an LEA from the list.");
      return;
    }

    setValidationError("");
    setSearchError("");
    setIsLoading(true);
    setHasSearched(true);

    try {
      const data = await fetchAgencyData(
        leaContext,
        selectedLeaId,
        lea.CDSCode,
        lea.LEAName,
        lea.VendorNumber
      );
      setResult(data);
    } catch (err) {
      setSearchError(
        err instanceof Error
          ? err.message
          : "An error occurred. Please try again."
      );
      setResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedLeaId("");
    setViewType(VIEW_TYPE_OPTIONS[0]?.Value ?? "Agency");
    setValidationError("");
    setSearchError("");
    setHasSearched(false);
    setResult(null);
  };

  return (
    <>
      <Card className="card-custom mb-5 shadow-none">
        <div className="mb-0 p-4 bg-white w-100">
          <div className="row g-3">
            <div className="col-12 col-md-5">
              <label
                htmlFor="lea-data-view-lea"
                className="form-label fw-semibold"
              >
                LEA
              </label>
              <SearchComboBox
                id="lea-data-view-lea"
                options={leaComboOptions}
                value={selectedLeaId}
                onChange={handleLeaChange}
                placeholder="Search for an LEA..."
                hasError={!!validationError}
                disabled={isLoading}
              />
            </div>

            <div className="col-12 col-md-3">
              <label
                htmlFor="lea-data-view-type"
                className="form-label fw-semibold"
              >
                View Type
              </label>
              <select
                id="lea-data-view-type"
                className="form-select"
                value={viewType}
                onChange={(e) => setViewType(e.target.value)}
                disabled={isLoading}
              >
                {VIEW_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.Value} value={opt.Value}>
                    {opt.Text}
                  </option>
                ))}
              </select>
            </div>

            {(validationError || searchError) && (
              <div className="col-12">
                <div
                  role="alert"
                  className="alert mb-0"
                  style={{
                    background: validationError ? "#fff3cd" : "#f8d7da",
                    border: `1px solid ${validationError ? "#ffc107" : "#f5c2c7"}`,
                    color: validationError ? "#856404" : "#842029",
                    borderRadius: "4px",
                    fontSize: "14px",
                    padding: "12px 16px",
                  }}
                >
                  {validationError || searchError}
                </div>
              </div>
            )}

            <div className="col-12 d-flex align-items-end gap-2 mt-4 mb-1">
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={handleSearch}
                disabled={isLoading}
              >
                <FiSearch aria-hidden="true" />
                {isLoading ? "Searching..." : "View"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleClear}
                disabled={isLoading}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </Card>

      {!hasSearched ? (
        <Card className="card-custom shadow-none">
          <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "#e9ecef",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "1rem",
              }}
            >
              <HiOutlineOfficeBuilding size={30} color="#6c757d" />
            </div>
            <h5 className="fw-semibold mb-2" style={{ color: "#0d2d4a" }}>
              Look Up LEA Data
            </h5>
            <p
              className="mb-0"
              style={{ maxWidth: 380, fontSize: "0.9rem", color: "#212529" }}
            >
              Select an LEA above, then click View to see its agency, site,
              and certification data.
            </p>
          </div>
        </Card>
      ) : isLoading ? (
        <Card className="card-custom shadow-none">
          <div className="d-flex align-items-center justify-content-center py-5 gap-3">
            <div
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
              style={{ color: "#0d2d4a" }}
            />
            <span style={{ color: "#0d2d4a" }}>Loading...</span>
          </div>
        </Card>
      ) : searchError ? null : !result ? (
        <Card className="card-custom shadow-none">
          <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
            <h5 className="fw-semibold mb-2" style={{ color: "#0d2d4a" }}>
              No Data Found
            </h5>
            <p
              className="mb-0"
              style={{ maxWidth: 380, fontSize: "0.9rem", color: "#212529" }}
            >
              No data was found for the selected LEA.
            </p>
          </div>
        </Card>
      ) : (
        <>
          <ReviewSection icon={<HiOutlineOfficeBuilding />} title="Agency">
            <div className="row">
              <ReviewField label="Agency Name" value={result.agencyName} />
              <ReviewField label="LEA ID" value={result.leaId} />
              <ReviewField label="Vendor Number" value={result.vendorNumber} />
            </div>
          </ReviewSection>

          <ReviewSection icon={<FiMapPin />} title="Sites">
            <DataTable
              columns={[
                "Site",
                "CDS Code",
                "Provider County",
                "Open Date",
                "Closure Date",
              ]}
              emptyMessage="No sites found for this LEA."
              rows={result.sites.map((site) => [
                site.siteName,
                site.cdsCode,
                site.providerCounty,
                formatDateOnly(site.openDate),
                site.closureDate ? formatDateOnly(site.closureDate) : "—",
              ])}
            />
          </ReviewSection>

          <ReviewSection icon={<FiCheckCircle />} title="Certification">
            <DataTable
              columns={[
                "Submission Cycle",
                "Certification Status",
                "Certifier Name",
                "Certifier Email",
                "Certifier Phone",
                "Total Enrollment",
              ]}
              emptyMessage="No certification data found for this LEA."
              rows={result.certifications.map((cert) => [
                cert.submissionCycle,
                <span
                  key="status"
                  style={{
                    ...badgeStyle,
                    ...STATUS_COLORS[cert.certificationStatus],
                  }}
                >
                  {cert.certificationStatus}
                </span>,
                cert.certifierName || "—",
                cert.certifierEmail || "—",
                cert.certifierPhone || "—",
                cert.totalEnrollment.toLocaleString(),
              ])}
            />
          </ReviewSection>
        </>
      )}
    </>
  );
}
